/**
 * "Draft document" (R1): the whole-document job's button on a response
 * document's overview — outline documents and single documents alike.
 *
 * The server owns the switch (DOCUMENT_JOB_ENABLED): until its status says
 * `enabled: true`, `available` is false (or null while unknown). While the
 * first read is in flight (`isCheckingAvailability`) the page shows neither
 * button; once it is off, or the read failed, it keeps its old Draft all
 * sections button. When on, this loads which sections
 * the job would draft for the user, starts it with exactly the writable ones,
 * and follows a job on this document through the shared jobs list (the tray's
 * poll), so a reload or a teammate's job shows the same state. Progress is
 * shown by the job's own card (PanelDocumentJob), not here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ApiRequestError,
  getDocumentJobEntryStatus,
  startDocumentJob,
  toastApiError,
  type DocumentJobEntrySection,
  type DocumentJobEntryStatus,
} from '@/lib/api'
import { useActiveJobsStore } from '@/stores/active-jobs-store'

type EnabledStatus = Extract<DocumentJobEntryStatus, { enabled: true }>

export interface UseDraftDocumentResult {
  /** True when the switch is on; false when off; null until known (or when the read failed). */
  available: boolean | null
  /**
   * True until the first status read for this document has answered (or
   * failed). The page renders no draft button meanwhile, so the old one can't
   * flash up and be clicked before the switch is known.
   */
  isCheckingAvailability: boolean
  status: EnabledStatus | null
  /** Sections the job would draft for the user (with text or without). */
  writableSections: DocumentJobEntrySection[]
  /** Why the others are skipped, one line per distinct reason. */
  skipReasons: string[]
  isStarting: boolean
  /** A job is waiting or running on this document. */
  isRunning: boolean
  start: () => Promise<void>
  /** Re-read the status (e.g. as the confirm dialog opens). */
  refresh: () => Promise<void>
}

function isActive(status: string | undefined): boolean {
  return status === 'pending' || status === 'running'
}

function readString(details: Record<string, unknown> | undefined, key: string): string | null {
  const value = details?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

export function useDraftDocument(
  artifactId: string | undefined,
  options: {
    enabled: boolean
    /**
     * Changes whenever the saved outline does (the server's outline version).
     * The status is re-read then, so a section added to the outline turns the
     * button on without a reload (UX2 01). Optional: a single document's
     * sections don't change on its overview.
     */
    outlineSignature?: string
    /**
     * A job this page watched run (waiting or running, or started here) on
     * this document reached a finished state: completed, failed or cancelled
     * (a partial outcome is a completed job). Not called for a job that had
     * already finished before the page saw it running. Use it to re-read
     * what the job wrote, e.g. the sections table.
     */
    onJobFinished?: () => void
  }
): UseDraftDocumentResult {
  const { enabled, outlineSignature, onJobFinished } = options
  const [available, setAvailable] = useState<boolean | null>(null)
  // The document whose first status read has answered (or failed).
  const [settledFor, setSettledFor] = useState<string | null>(null)
  const [status, setStatus] = useState<EnabledStatus | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  // The job this page just started (or was told is running), until the jobs list has it.
  const [startedJobId, setStartedJobId] = useState<string | null>(null)

  const listedJob = useActiveJobsStore((s) => (artifactId ? s.getDocumentJobForArtifact(artifactId) : null))

  // Only the latest read lands: an older answer arriving late must not undo a newer one.
  const readSeq = useRef(0)
  const refresh = useCallback(async (): Promise<void> => {
    if (!artifactId) return
    const seq = ++readSeq.current
    try {
      const next = await getDocumentJobEntryStatus(artifactId)
      if (seq !== readSeq.current) return
      if (next.enabled) {
        setAvailable(true)
        setStatus(next)
      } else {
        setAvailable(false)
        setStatus(null)
      }
    } catch {
      if (seq !== readSeq.current) return
      // Unknown: the page keeps the old button; nothing else depends on this.
      setAvailable(null)
      setStatus(null)
    }
    setSettledFor(artifactId)
  }, [artifactId])

  useEffect(() => {
    setAvailable(null)
    setStatus(null)
    setStartedJobId(null)
    setSettledFor(null)
    if (!enabled || !artifactId) return
    void refresh()
  }, [enabled, artifactId, refresh])

  // The saved outline changed: re-read, so the sections and the button follow
  // it. Keeps the current status meanwhile (no flash back to the old button).
  const readSignature = useRef(outlineSignature)
  useEffect(() => {
    if (readSignature.current === outlineSignature) return
    readSignature.current = outlineSignature
    if (!enabled || !artifactId) return
    void refresh()
  }, [enabled, artifactId, outlineSignature, refresh])

  // A job on this document ended: re-read, so the sections and the button are current.
  const listedStatus = listedJob?.status
  useEffect(() => {
    if (!enabled || !listedStatus || isActive(listedStatus)) return
    void refresh()
  }, [enabled, listedStatus, refresh])

  // Tell the page when a job it watched finishes. "Watched" = seen waiting or
  // running in the jobs list, started here, or named running by the status; a
  // job already finished when the page first saw it is not reported. Each job
  // is reported once.
  const onJobFinishedRef = useRef(onJobFinished)
  onJobFinishedRef.current = onJobFinished
  const watchedJobIds = useRef(new Set<string>())
  const reportedJobIds = useRef(new Set<string>())
  const listedJobId = listedJob?.jobId
  const statusActiveJobId = status?.activeJobId ?? null
  useEffect(() => {
    if (startedJobId) watchedJobIds.current.add(startedJobId)
    if (statusActiveJobId) watchedJobIds.current.add(statusActiveJobId)
    if (!enabled || !listedJobId || !listedStatus) return
    if (isActive(listedStatus)) {
      watchedJobIds.current.add(listedJobId)
      return
    }
    if (!watchedJobIds.current.has(listedJobId) || reportedJobIds.current.has(listedJobId)) return
    reportedJobIds.current.add(listedJobId)
    onJobFinishedRef.current?.()
  }, [enabled, listedJobId, listedStatus, startedJobId, statusActiveJobId])

  // Running: the jobs list shows a job on this document waiting or running, or
  // a job this page started (or the status named) is not in the list yet. Once
  // the list has that job, its status decides.
  const listedActive = listedJob !== null && isActive(listedJob.status)
  const notYetListed = [startedJobId, status?.activeJobId ?? null]
    .some((jobId) => jobId !== null && jobId !== listedJob?.jobId)
  const isRunning = listedActive || notYetListed

  const writableSections = useMemo(
    () => status?.sections.filter((section) => section.state === 'writable') ?? [],
    [status]
  )
  const skipReasons = useMemo(
    () => Array.from(new Set(
      (status?.sections ?? [])
        .filter((section) => section.state === 'not_writable')
        .map((section) => section.skipReason ?? `Skipped ${section.title}.`)
    )),
    [status]
  )

  const start = useCallback(async () => {
    if (!artifactId || writableSections.length === 0 || isRunning || isStarting) return
    setIsStarting(true)
    try {
      const started = await startDocumentJob(artifactId, writableSections.map((section) => section.sectionKey))
      setStartedJobId(started.jobId)
      useActiveJobsStore.getState().notifyJobDispatched()
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'DOCUMENT_JOB_BUSY') {
        // Someone (maybe this user, in another tab) already started it: follow that one.
        toast.info('This document is already being drafted.')
        const runningJobId = readString(error.details, 'jobId')
        if (runningJobId) setStartedJobId(runningJobId)
        else void refresh()
        useActiveJobsStore.getState().notifyJobDispatched()
      } else if (error instanceof ApiRequestError && error.code === 'NOTHING_TO_DRAFT') {
        toast.info('Those sections changed, so there is nothing to draft for you now.')
        void refresh()
      } else {
        toastApiError(error, "Couldn't start drafting. Please try again.")
      }
    } finally {
      setIsStarting(false)
    }
  }, [artifactId, writableSections, isRunning, isStarting, refresh])

  const isCheckingAvailability = enabled && Boolean(artifactId) && settledFor !== artifactId

  return { available, isCheckingAvailability, status, writableSections, skipReasons, isStarting, isRunning, start, refresh }
}
